import type { SourceDomain } from "./sourceDomain.js";

const API_BASE = import.meta.env.VITE_API_BASE || "";

export interface CreateBatchRequest {
  clientName: string;
  clientEmail: string;
  sourceDomain: SourceDomain;
  files: Array<{ name: string; size: number; sha256: string; mime: string }>;
}

export interface CreateBatchResponse {
  batchId: string;
  token: string;
  sourceDomain: SourceDomain;
  zohoFolderId: string;
  folderUrl: string;
  files: Array<{
    id: string;
    originalName: string;
    storedName: string;
    status: "pending" | "uploading" | "success" | "failed";
    sizeBytes: number;
  }>;
}

export interface BatchStateResponse {
  batchId: string;
  sourceDomain: SourceDomain;
  status: "pending" | "in_progress" | "complete" | "partial_failed";
  zohoFolderId: string | null;
  folderUrl: string | null;
  totalFiles: number;
  files: Array<{
    id: string;
    originalName: string;
    storedName: string;
    status: "pending" | "uploading" | "success" | "failed";
    sizeBytes: number;
    errorMessage: string | null;
    attempts: number;
  }>;
}

export interface FinalizeResponse {
  sourceDomain: SourceDomain;
  status: "complete" | "partial_failed";
  succeeded: number;
  failed: number;
  folderUrl: string | null;
}

export async function createBatch(
  body: CreateBatchRequest,
): Promise<CreateBatchResponse> {
  const res = await fetch(`${API_BASE}/api/batch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j.error || `create batch failed: ${res.status}`);
  }
  return res.json();
}

export async function getBatch(
  batchId: string,
  token: string,
): Promise<BatchStateResponse> {
  const res = await fetch(`${API_BASE}/api/batch/${batchId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`get batch failed: ${res.status}`);
  return res.json();
}

export async function finalizeBatch(
  batchId: string,
  token: string,
): Promise<FinalizeResponse> {
  const res = await fetch(`${API_BASE}/api/batch/${batchId}/finalize`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j.error || `finalize failed: ${res.status}`);
  }
  return res.json();
}

export function uploadFileXHR(opts: {
  batchId: string;
  fileId: string;
  token: string;
  file: File;
  onProgress: (pct: number) => void;
}): Promise<{ ok: boolean; status: number; error?: string; retryAfter?: number }> {
  const { batchId, fileId, token, file, onProgress } = opts;
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", `${API_BASE}/api/batch/${batchId}/file/${fileId}`);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) {
        onProgress(Math.round((ev.loaded / ev.total) * 100));
      }
    };
    xhr.onload = () => {
      let body: { error?: string } = {};
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        /* ignore */
      }
      const retryAfterHdr = xhr.getResponseHeader("Retry-After");
      const retryAfter = retryAfterHdr ? Number(retryAfterHdr) : undefined;
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        error: body.error,
        retryAfter,
      });
    };
    xhr.onerror = () => resolve({ ok: false, status: 0, error: "network_error" });
    xhr.ontimeout = () => resolve({ ok: false, status: 0, error: "timeout" });

    const form = new FormData();
    form.append("file", file, file.name);
    xhr.send(form);
  });
}
